import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { motion, useInView } from "framer-motion";
import { ViraLensLogo } from "@/components/ViraLensLogo";
import { BackgroundEffects, SectionDivider } from "@/components/BackgroundEffects";
import { SEO } from "@/components/SEO";
import {
  Search,
  Sparkles,
  FileText,
  TrendingUp,
  Zap,
  BarChart3,
  Check,
  ChevronDown,
  Menu,
  X,
  ArrowRight,
  Globe,
  Shield,
  Users,
  Building2,
  Crown,
  Star,
} from "lucide-react";
import { FaTiktok, FaInstagram } from "react-icons/fa";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { formatCurrency } from "@/lib/format";

function FadeInSection({ children, className, delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, ease: "easeOut", delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function Navbar() {
  const { t } = useTranslation(["common", "landing"]);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id: string) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header>
    <nav aria-label="Main navigation" className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      scrolled ? "bg-[#07070a]/90 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
    )}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <ViraLensLogo height={32} />

          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo("features")} className="text-sm text-white/60 hover:text-white transition-colors">{t("common:features")}</button>
            <button onClick={() => scrollTo("pricing")} className="text-sm text-white/60 hover:text-white transition-colors">{t("common:pricing")}</button>
            <button onClick={() => scrollTo("faq")} className="text-sm text-white/60 hover:text-white transition-colors">{t("common:faq")}</button>
            <Link href="/login" className="text-sm text-white/60 hover:text-white transition-colors">{t("common:login")}</Link>
            <LanguageSwitcher variant="landing" />
            <Link href="/register" className="px-5 py-2.5 bg-gradient-to-r from-vl-orange to-vl-deep text-white text-sm font-semibold rounded-xl hover:opacity-90 transition-all shadow-lg shadow-vl-deep/25">
              {t("common:getStarted")}
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-white/60 hover:text-white p-2 -mr-2"
            aria-label={mobileOpen ? t("common:closeMenu") : t("common:openMenu")}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-[#0c0c10]/98 backdrop-blur-xl border-b border-white/5 px-4 pb-6 pt-2"
        >
          <div className="flex flex-col gap-4">
            <button onClick={() => scrollTo("features")} className="text-left text-sm text-white/60 hover:text-white py-2">{t("common:features")}</button>
            <button onClick={() => scrollTo("pricing")} className="text-left text-sm text-white/60 hover:text-white py-2">{t("common:pricing")}</button>
            <button onClick={() => scrollTo("faq")} className="text-left text-sm text-white/60 hover:text-white py-2">{t("common:faq")}</button>
            <Link href="/login" className="text-sm text-white/60 hover:text-white py-2">{t("common:login")}</Link>
            <LanguageSwitcher variant="landing" />
            <Link href="/register" className="px-5 py-3 bg-gradient-to-r from-vl-orange to-vl-deep text-white text-sm font-semibold rounded-xl text-center">
              {t("common:getStarted")}
            </Link>
          </div>
        </motion.div>
      )}
    </nav>
    </header>
  );
}

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");
const HERO_CARDS = [
  { image: `${BASE_URL}/images/hero-card-1.png?v=5`, rotate: -6, offsetY: 10, alt: "Viral TikTok — Oryzo by @webloved, 1.5M views" },
  { image: `${BASE_URL}/images/hero-card-2.png?v=5`, rotate: 3, offsetY: -8, alt: "Viral TikTok — Claude Skills by @samdespo, 564.8K views" },
  { image: `${BASE_URL}/images/hero-card-3.png?v=5`, rotate: -2, offsetY: 14, alt: "Viral TikTok — Three by @adrien.ninet, 458.7K views" },
  { image: `${BASE_URL}/images/hero-card-4.png?v=5`, rotate: 5, offsetY: -4, alt: "Viral TikTok — 5 Things With Claude by @taki.gpt, 412.6K views" },
  { image: `${BASE_URL}/images/hero-card-5.png?v=5`, rotate: -4, offsetY: 8, alt: "Viral TikTok — Claude AI by @alex.bobko5, 342.9K views" },
];

function HeroSection() {
  const { t } = useTranslation(["landing", "common"]);
  return (
    <section className="relative flex flex-col items-center justify-center pt-28 sm:pt-32 pb-0 overflow-hidden">
      <div className="hero-glow-ring" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-extrabold text-white leading-[1.1] tracking-tight">
            {t("landing:hero.title1")}{" "}
            <span className="italic font-serif text-vl-orange">{t("landing:hero.titleIn")}</span>{" "}
            {t("landing:hero.title2")}
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
          className="mt-6 text-base sm:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed"
        >
          {t("landing:hero.subtitle")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
          className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link href="/register" className="px-8 py-3.5 bg-gradient-to-r from-vl-orange to-vl-deep text-white font-semibold rounded-xl shadow-lg shadow-vl-deep/30 hover:opacity-90 transition-all text-sm sm:text-base flex items-center gap-2">
            {t("landing:hero.cta")} <ArrowRight className="w-4 h-4" />
          </Link>
          <button
            onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
            className="px-8 py-3.5 border border-white/15 text-white/70 hover:text-white hover:border-white/30 rounded-xl transition-all text-sm sm:text-base font-medium"
          >
            {t("common:learnMore")}
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-6 text-xs text-white/30 flex items-center justify-center gap-1.5"
        >
          <Shield className="w-3.5 h-3.5" />
          {t("landing:hero.noCreditCard")}
        </motion.p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
        className="relative mt-12 w-full max-w-5xl mx-auto px-4 sm:px-6"
      >
        <div className="flex items-end justify-center gap-3 sm:gap-4 pb-12 px-2">
          {HERO_CARDS.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: card.offsetY, rotate: card.rotate }}
              transition={{ duration: 0.7, delay: 0.6 + i * 0.1, ease: "easeOut" }}
              className={cn(
                "relative shrink-0 w-[calc(33.333%-8px)] sm:w-[calc(25%-8px)] lg:w-[calc(20%-8px)] min-w-[56px] max-w-[156px]",
                i === 3 ? "hidden sm:block" : "",
                i === 4 ? "hidden lg:block" : "",
              )}
            >
              <div className="rounded-xl border border-white/[0.08] overflow-hidden shadow-2xl shadow-black/40 ring-1 ring-white/[0.05] relative">
                <img
                  src={card.image}
                  alt={card.alt}
                  className="block w-full h-auto"
                  loading="lazy"
                />
              </div>
            </motion.div>
          ))}
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#07070a] to-transparent pointer-events-none" />
      </motion.div>
    </section>
  );
}

const SHOWCASE_KEYS = ["tiktokTrends", "instagramReels", "viralAnalysis", "contentBriefs", "aiInsights", "engagementData", "creatorResearch", "trendDiscovery"] as const;
const SHOWCASE_ICONS = [FaTiktok, FaInstagram, TrendingUp, FileText, Sparkles, BarChart3, Users, Search];
const SHOWCASE_COLORS = ["text-white", "text-pink-400", "text-vl-orange", "text-vl-amber", "text-purple-400", "text-emerald-400", "text-blue-400", "text-vl-light"];

function ShowcaseStrip() {
  const { t } = useTranslation("landing");
  const items = SHOWCASE_KEYS.map((key, i) => ({
    label: t(`showcase.${key}`),
    icon: SHOWCASE_ICONS[i],
    color: SHOWCASE_COLORS[i],
  }));

  return (
    <section className="relative py-8 overflow-hidden border-y border-white/5">
      <div className="absolute inset-0 bg-gradient-to-r from-[#07070a] via-transparent to-[#07070a] z-10 pointer-events-none" />
      <div className="flex animate-[scroll_25s_linear_infinite] gap-8 w-max">
        {[...items, ...items, ...items].map((item, i) => {
          const Icon = item.icon;
          return (
            <div key={i} className="flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/[0.03] border border-white/5 shrink-0">
              <Icon className={cn("w-5 h-5", item.color)} />
              <span className="text-sm text-white/60 font-medium whitespace-nowrap">{item.label}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const FEATURE_META = [
  { icon: Sparkles, customIcon: null as null | "platforms", key: "aiAnalysis", glowColor: "group-hover:shadow-vl-orange/20" },
  { icon: Globe, customIcon: "platforms" as null | "platforms", key: "platformDiscovery", glowColor: "group-hover:shadow-purple-500/20" },
  { icon: FileText, customIcon: null as null | "platforms", key: "creativeBriefs", glowColor: "group-hover:shadow-vl-amber/20" },
];

function ValueProposition() {
  const { t } = useTranslation("landing");
  const FEATURES = FEATURE_META.map((f) => ({
    ...f,
    title: t(`features.${f.key}.title`),
    description: t(`features.${f.key}.description`),
  }));

  return (
    <section id="features" className="py-24 sm:py-32 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeInSection className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight">
            {t("features.heading")}
            <br />
            <span className="text-gradient-brand">{t("features.headingHighlight")}</span>
          </h2>
          <p className="mt-5 text-white/40 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            {t("features.subtitle")}
          </p>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((feature, i) => (
            <FadeInSection key={i} delay={i * 0.1}>
              <div className={cn(
                "group p-6 rounded-2xl border border-white/[0.08] bg-white/[0.03] backdrop-blur-md hover:bg-white/[0.06] transition-all duration-500 relative overflow-hidden",
                "hover:border-vl-orange/30 hover:shadow-[0_0_50px_-10px] " + feature.glowColor,
              )}>
                <div className="absolute inset-0 bg-gradient-to-br from-vl-orange/0 to-vl-deep/0 group-hover:from-vl-orange/[0.06] group-hover:to-vl-deep/[0.03] transition-all duration-500 pointer-events-none" />
                <div className="relative">
                  {feature.customIcon === "platforms" ? (
                    <div className="flex flex-row items-center gap-3 mb-4">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-white/[0.08] to-white/[0.03] border border-white/[0.1] flex items-center justify-center shrink-0 group-hover:border-white/20 group-hover:from-white/[0.12] transition-all duration-300 shadow-lg shadow-black/20">
                        <FaTiktok className="w-[18px] h-[18px] text-white" />
                      </div>
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-pink-500/15 to-purple-500/10 border border-pink-500/20 flex items-center justify-center shrink-0 group-hover:border-pink-500/35 group-hover:from-pink-500/20 transition-all duration-300 shadow-lg shadow-pink-500/10">
                        <FaInstagram className="w-[18px] h-[18px] text-pink-400" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-vl-deep/10 border border-vl-deep/20 flex items-center justify-center mb-4 group-hover:bg-vl-deep/20 group-hover:border-vl-orange/30 transition-all duration-300">
                      <feature.icon className="w-5 h-5 text-vl-orange" />
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-white/40 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductDemo() {
  const { t } = useTranslation("landing");
  return (
    <section className="py-16 sm:py-24 relative overflow-hidden">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] bg-vl-orange/[0.04] rounded-full blur-[150px] pointer-events-none" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeInSection>
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-vl-orange/70 font-semibold mb-3">{t("demo.label")}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white">{t("demo.heading")}</h2>
          </div>
        </FadeInSection>
        <FadeInSection delay={0.15}>
          <div className="relative group">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-b from-vl-orange/15 via-transparent to-vl-deep/15 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" />
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-b from-vl-orange/25 via-white/[0.06] to-vl-deep/25 opacity-50 group-hover:opacity-80 transition-opacity duration-500" />
            <div className="relative rounded-2xl border border-white/[0.08] bg-[#08080c] overflow-hidden shadow-2xl shadow-black/60">
              <img
                src={`${import.meta.env.BASE_URL}images/app-demo-screenshot.png`}
                alt="ViraLens app showing AI agent search results from TikTok and Instagram with real engagement metrics"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080c]/40 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </FadeInSection>
      </div>
    </section>
  );
}

const STEP_KEYS = ["step1", "step2", "step3"] as const;
const STEPS = [
  {
    step: "01",
    icon: Search,
    titleKey: "step1",
    descKey: "step1",
    accent: "from-vl-orange/15 to-vl-deep/10",
    borderHover: "hover:border-vl-orange/40",
    iconBg: "bg-gradient-to-br from-vl-orange/20 to-vl-deep/15 border-vl-orange/25",
    stepColor: "text-vl-orange/30",
    restBg: "bg-gradient-to-br from-vl-orange/[0.07] to-vl-deep/[0.04]",
    restBorder: "border-vl-orange/15",
  },
  {
    step: "02",
    icon: Sparkles,
    titleKey: "step2",
    descKey: "step2",
    accent: "from-purple-500/10 to-violet-600/8",
    borderHover: "hover:border-purple-500/30",
    iconBg: "bg-gradient-to-br from-purple-500/20 to-violet-600/15 border-purple-500/25",
    stepColor: "text-purple-500/30",
    restBg: "",
    restBorder: "border-white/[0.08]",
  },
  {
    step: "03",
    icon: FileText,
    titleKey: "step3",
    descKey: "step3",
    accent: "from-emerald-500/10 to-teal-600/8",
    borderHover: "hover:border-emerald-500/30",
    iconBg: "bg-gradient-to-br from-emerald-500/20 to-teal-600/15 border-emerald-500/25",
    stepColor: "text-emerald-500/30",
    restBg: "",
    restBorder: "border-white/[0.08]",
  },
];

function HowItWorks() {
  const { t } = useTranslation("landing");
  return (
    <section className="py-24 sm:py-32 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeInSection className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight">
            {t("howItWorks.heading")}
            <br />
            <span className="text-gradient-brand">{t("howItWorks.headingHighlight")}</span>
          </h2>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {STEPS.map((step, i) => (
            <FadeInSection key={i} delay={i * 0.12}>
              <div className="relative group h-full">
                <div className={cn(
                  "p-8 rounded-2xl border transition-all duration-500 h-full relative overflow-hidden backdrop-blur-md",
                  step.restBorder,
                  step.restBg || "bg-white/[0.03]",
                  step.borderHover,
                )}>
                  <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none", step.accent)} />
                  <div className="relative">
                    <div className={cn("text-5xl font-extrabold mb-5 font-display", step.stepColor)}>{step.step}</div>
                    <div className={cn("w-14 h-14 rounded-xl border flex items-center justify-center mb-5", step.iconBg)}>
                      <step.icon className="w-6 h-6 text-white/80" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{t(`howItWorks.${step.titleKey}.title`)}</h3>
                    <p className="text-sm text-white/40 leading-relaxed">{t(`howItWorks.${step.descKey}.description`)}</p>
                  </div>
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsBanner() {
  const { t } = useTranslation("landing");
  const stats = [
    { value: "500+", label: t("stats.postsAnalyzed"), icon: Search },
    { value: "2", label: t("stats.platformsSupported"), icon: Globe },
    { value: "60s", label: t("stats.briefGeneration"), icon: Zap },
    { value: "100%", label: t("stats.realData"), icon: BarChart3 },
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-vl-deep/25 via-vl-orange/20 to-vl-deep/25" />
      <div className="absolute inset-0 bg-[#07070a]/50" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vl-orange/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vl-orange/50 to-transparent" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <FadeInSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12">
            {stats.map((stat, i) => (
              <div key={i} className="text-center group">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-vl-orange/10 border border-vl-orange/20 mb-4 group-hover:bg-vl-orange/15 transition-all">
                  <stat.icon className="w-5 h-5 text-vl-orange" />
                </div>
                <div className="text-3xl sm:text-4xl font-extrabold text-gradient-brand mb-1">{stat.value}</div>
                <p className="text-xs sm:text-sm text-white/40">{stat.label}</p>
              </div>
            ))}
          </div>
        </FadeInSection>
      </div>
    </section>
  );
}

interface LandingPlan {
  name: string;
  planKey: string;
  price: string;
  period: string;
  icon: React.ElementType;
  popular: boolean;
  features?: string[];
  fullPrice?: string;
  monthlyEquivalent?: string;
  savings?: string;
  rawPrice?: number;
  rawFullPrice?: number;
  rawMonthlyEquivalent?: number;
  rawSavings?: number;
  billing?: "monthly" | "annual";
}

interface EnterprisePlan {
  name: string;
  planKey: string;
  price: string;
  period: string;
  icon: React.ElementType;
  popular: boolean;
  features?: string[];
  credits?: string;
}

const MONTHLY_PLANS: Omit<LandingPlan, "features">[] = [
  { name: "", planKey: "pro", price: "", period: "", icon: Zap, popular: false, rawPrice: 49, billing: "monthly" },
  { name: "", planKey: "agency", price: "", period: "", icon: Building2, popular: true, rawPrice: 99, billing: "monthly" },
];

const ANNUAL_PLANS: Omit<LandingPlan, "features">[] = [
  { name: "", planKey: "pro", price: "", period: "", icon: Zap, popular: false, rawPrice: 297, rawFullPrice: 588, rawMonthlyEquivalent: 24.75, rawSavings: 291, billing: "annual" },
  { name: "", planKey: "agency", price: "", period: "", icon: Building2, popular: true, rawPrice: 600, rawFullPrice: 1188, rawMonthlyEquivalent: 50, rawSavings: 588, billing: "annual" },
];

const ENTERPRISE_BASE: Omit<EnterprisePlan, "features" | "credits"> = {
  name: "",
  planKey: "enterprise",
  price: "",
  period: "",
  icon: Crown,
  popular: false,
};

function PricingSection() {
  const { t } = useTranslation("landing");
  const [isAnnual, setIsAnnual] = useState(false);
  const plans = isAnnual ? ANNUAL_PLANS : MONTHLY_PLANS;
  const enterprise = ENTERPRISE_BASE;

  return (
    <section id="pricing" className="py-24 sm:py-32 relative">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeInSection className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight">
            {t("pricing.heading")}
            <br />
            <span className="text-gradient-brand">{t("pricing.headingHighlight")}</span>
          </h2>
          <p className="mt-4 text-white/40 text-base sm:text-lg">
            {t("pricing.subtitle")}
          </p>
        </FadeInSection>

        <FadeInSection className="flex items-center justify-center gap-3 mb-12">
          <div className="relative flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1">
            <button
              onClick={() => setIsAnnual(false)}
              className={cn(
                "relative z-10 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300",
                !isAnnual ? "text-white" : "text-white/40 hover:text-white/60"
              )}
            >
              {t("pricing.monthly")}
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={cn(
                "relative z-10 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300",
                isAnnual ? "text-white" : "text-white/40 hover:text-white/60"
              )}
            >
              {t("pricing.annual")}
            </button>
            <motion.div
              className="absolute top-1 bottom-1 rounded-full bg-gradient-to-r from-vl-deep to-vl-orange"
              layout
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              style={{
                left: isAnnual ? "calc(50% - 2px)" : "4px",
                width: isAnnual ? "calc(50% - 2px)" : "calc(50% - 2px)",
              }}
            />
          </div>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, i) => (
            <FadeInSection key={`${isAnnual ? "annual" : "monthly"}-${plan.planKey}`} delay={i * 0.1}>
              <div className={cn(
                "relative rounded-2xl overflow-hidden h-full",
                plan.popular ? "p-px bg-gradient-to-b from-vl-orange via-vl-deep/60 to-vl-orange/30" : "p-px bg-gradient-to-b from-white/10 via-white/5 to-white/[0.02]"
              )}>
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-vl-orange to-vl-deep text-center py-1.5 text-xs font-bold text-white z-10 flex items-center justify-center gap-1.5">
                    <Star className="w-3.5 h-3.5 fill-white" />
                    {t("pricing.mostPopular")}
                  </div>
                )}
                {isAnnual && (
                  <div className="absolute top-0 right-0 overflow-hidden w-[5.5rem] h-[5.5rem] pointer-events-none z-20">
                    <div className="absolute top-[14px] right-[-24px] rotate-45 bg-green-500 text-black text-[9px] font-extrabold py-[3px] w-[110px] text-center shadow-md tracking-wider uppercase">
                      {t("pricing.discount")}
                    </div>
                  </div>
                )}
                <div className={cn(
                  "rounded-2xl bg-[#0c0c10]/90 backdrop-blur-md p-6 sm:p-8 h-full flex flex-col",
                  plan.popular ? "pt-10 sm:pt-12" : ""
                )}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      plan.popular ? "bg-vl-orange/15" : "bg-white/5"
                    )}>
                      <plan.icon className={cn("w-5 h-5", plan.popular ? "text-vl-orange" : "text-white/50")} />
                    </div>
                    <div>
                      <p className="text-sm text-white/40">ViraLens</p>
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-bold text-white">{t(`pricing.plans.${plan.planKey}.name`)}</p>
                        {isAnnual && plan.rawSavings && (
                          <span className="text-[10px] font-bold text-green-400 bg-green-500/10 border border-green-500/20 rounded-full px-2 py-0.5">
                            {t("pricing.save", { amount: formatCurrency(plan.rawSavings) })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mb-1">
                    {isAnnual && plan.rawFullPrice && (
                      <span className="text-lg text-white/25 line-through mr-2">{formatCurrency(plan.rawFullPrice)}</span>
                    )}
                    <span className="text-4xl font-extrabold text-white">{plan.rawPrice != null ? formatCurrency(plan.rawPrice) : plan.price}</span>
                    <span className="text-white/40 text-sm">{plan.billing === "annual" ? t("pricing.perYear") : t("pricing.perMonth")}</span>
                  </div>
                  {isAnnual && plan.rawMonthlyEquivalent ? (
                    <p className="text-xs text-vl-orange/70 mb-6">
                      {t("pricing.effectiveMonthly", { price: formatCurrency(plan.rawMonthlyEquivalent) })} · {t(`pricing.plans.${plan.planKey}.credits`)}
                    </p>
                  ) : (
                    <p className="text-xs text-vl-orange/70 mb-6">{t(`pricing.plans.${plan.planKey}.credits`)}</p>
                  )}

                  <Link
                    href="/register"
                    className={cn(
                      "w-full py-3 rounded-xl text-center text-sm font-semibold transition-all mb-6 block",
                      plan.popular
                        ? "bg-gradient-to-r from-vl-orange to-vl-deep text-white shadow-lg shadow-vl-deep/25 hover:opacity-90"
                        : "border border-white/15 text-white/70 hover:text-white hover:border-white/30 hover:bg-white/5"
                    )}
                  >
                    {t("pricing.joinPlan", { plan: t(`pricing.plans.${plan.planKey}.name`) })}
                  </Link>

                  <ul className="space-y-3 flex-1">
                    {(t(`pricing.plans.${plan.planKey}.features`, { returnObjects: true }) as string[]).map((feature, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-sm text-white/50">
                        <Check className="w-4 h-4 shrink-0 text-vl-orange/70 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeInSection>
          ))}

          <FadeInSection delay={0.2}>
            <div className="relative rounded-2xl overflow-hidden h-full p-px bg-gradient-to-b from-white/10 via-white/5 to-white/[0.02]">
              <div className="rounded-2xl bg-[#0c0c10]/90 backdrop-blur-md p-6 sm:p-8 h-full flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/5">
                    <enterprise.icon className="w-5 h-5 text-white/50" />
                  </div>
                  <div>
                    <p className="text-sm text-white/40">ViraLens</p>
                    <p className="text-lg font-bold text-white">{t("pricing.plans.enterprise.name")}</p>
                  </div>
                </div>

                <div className="mb-1">
                  <span className="text-4xl font-extrabold text-white">{t("pricing.enterprise.custom")}</span>
                </div>
                <p className="text-xs text-vl-orange/70 mb-6">{t("pricing.plans.enterprise.credits")}</p>

                <Link
                  href="mailto:hello@viralens.io?subject=ViraLens Enterprise Inquiry"
                  className="w-full py-3 rounded-xl text-center text-sm font-semibold transition-all mb-6 block border border-white/15 text-white/70 hover:text-white hover:border-white/30 hover:bg-white/5"
                >
                  {t("pricing.enterprise.contactSales")}
                </Link>

                <ul className="space-y-3 flex-1">
                  {(t("pricing.plans.enterprise.features", { returnObjects: true }) as string[]).map((feature, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-white/50">
                      <Check className="w-4 h-4 shrink-0 text-vl-orange/70 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FadeInSection>
        </div>

        <FadeInSection className="text-center mt-8">
          <p className="text-sm text-white/30">
            {t("pricing.moreCredits")}
          </p>
        </FadeInSection>
      </div>
    </section>
  );
}


function FAQSection() {
  const { t } = useTranslation("landing");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-24 sm:py-32 relative">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <FadeInSection className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white">
            {t("faqSection.heading")}{" "}
            <span className="text-gradient-brand">{t("faqSection.headingHighlight")}</span>
          </h2>
        </FadeInSection>

        <div className="space-y-3">
          {(t("faqSection.items", { returnObjects: true }) as { q: string; a: string }[]).map((item, i) => (
            <FadeInSection key={i} delay={i * 0.05}>
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] backdrop-blur-sm overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="text-sm sm:text-base font-semibold text-white pr-4">{item.q}</span>
                  <ChevronDown className={cn(
                    "w-5 h-5 text-white/30 shrink-0 transition-transform duration-300",
                    openIndex === i ? "rotate-180" : ""
                  )} />
                </button>
                <motion.div
                  initial={false}
                  animate={{
                    height: openIndex === i ? "auto" : 0,
                    opacity: openIndex === i ? 1 : 0,
                  }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <p className="px-5 pb-5 text-sm text-white/40 leading-relaxed">{item.a}</p>
                </motion.div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const { t } = useTranslation("landing");
  return (
    <section className="py-24 sm:py-32 relative overflow-hidden">
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <FadeInSection>
          <div className="mb-10 flex items-center justify-center">
            <div className="relative">
              <div className="absolute -inset-4 rounded-2xl bg-vl-orange/20 blur-xl animate-pulse" />
              <div className="absolute -inset-8 rounded-3xl bg-vl-deep/10 blur-2xl" />
              <div className="relative">
                <ViraLensLogo height={56} />
              </div>
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight">
            {t("cta.heading")}
            <br />
            <span className="text-gradient-brand">{t("cta.headingHighlight")}</span>
          </h2>
          <p className="mt-5 text-white/40 max-w-md mx-auto">
            {t("cta.subtitle")}
          </p>
          <div className="mt-8">
            <Link href="/register" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-vl-orange to-vl-deep text-white font-semibold rounded-xl shadow-lg shadow-vl-deep/30 hover:shadow-vl-deep/50 hover:scale-[1.02] transition-all duration-300 text-sm sm:text-base">
              {t("cta.button")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-5 text-xs text-white/25 flex items-center justify-center gap-1.5">
            <Shield className="w-3.5 h-3.5" />
            {t("hero.noCreditCard")}
          </p>
        </FadeInSection>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useTranslation(["landing", "common"]);
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="border-t border-white/5 bg-[#07070a]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
          <div className="col-span-2 sm:col-span-1">
            <ViraLensLogo height={28} />
            <p className="mt-3 text-xs text-white/25 leading-relaxed">
              {t("landing:footer.description")}
            </p>
          </div>

          <div>
            <h4 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-4">{t("landing:footer.product")}</h4>
            <ul className="space-y-2.5">
              <li><button onClick={() => scrollTo("features")} className="text-sm text-white/30 hover:text-white/60 transition-colors">{t("common:features")}</button></li>
              <li><button onClick={() => scrollTo("pricing")} className="text-sm text-white/30 hover:text-white/60 transition-colors">{t("common:pricing")}</button></li>
              <li><button onClick={() => scrollTo("faq")} className="text-sm text-white/30 hover:text-white/60 transition-colors">{t("common:faq")}</button></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-4">ViraLens</h4>
            <ul className="space-y-2.5">
              <li><Link href="/register" className="text-sm text-white/30 hover:text-white/60 transition-colors">{t("common:getStarted")}</Link></li>
              <li><Link href="/login" className="text-sm text-white/30 hover:text-white/60 transition-colors">{t("common:login")}</Link></li>
              <li><Link href="/support" className="text-sm text-white/30 hover:text-white/60 transition-colors">{t("landing:footer.support")}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-4">{t("landing:footer.legal")}</h4>
            <ul className="space-y-2.5">
              <li><Link href="/terms" className="text-sm text-white/30 hover:text-white/60 transition-colors">{t("landing:footer.terms")}</Link></li>
              <li><Link href="/privacy" className="text-sm text-white/30 hover:text-white/60 transition-colors">{t("landing:footer.privacy")}</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 text-center">
          <p className="text-xs text-white/20">
            {t("common:copyright", { year: new Date().getFullYear() })}
          </p>
          <p className="text-xs text-white/15 mt-1">
            {t("common:poweredBy")}{" "}
            <a href="https://hackstack.io" target="_blank" rel="noopener noreferrer" className="hover:text-white/30 transition-colors">
              Hackstack.io
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

const LANDING_JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "ViraLens",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "AI-powered content research and creative brief platform for marketing agencies. Scrape TikTok and Instagram for viral content, analyze trends with Gemini AI, and generate ready-to-use creative briefs.",
    url: "https://app.viralens.ai",
    offers: [
      {
        "@type": "Offer",
        name: "Pro Monthly",
        price: "49.00",
        priceCurrency: "USD",
        billingDuration: "P1M",
        description: "2,000 credits per month, 5 workspaces, AI analysis, creative brief generation",
      },
      {
        "@type": "Offer",
        name: "Agency Monthly",
        price: "99.00",
        priceCurrency: "USD",
        billingDuration: "P1M",
        description: "5,000 credits per month, 20 workspaces, team collaboration, priority support",
      },
      {
        "@type": "Offer",
        name: "Pro Annual",
        price: "297.00",
        priceCurrency: "USD",
        billingDuration: "P1Y",
        description: "25,000 credits upfront, 5 workspaces, 50% savings vs monthly",
      },
      {
        "@type": "Offer",
        name: "Agency Annual",
        price: "600.00",
        priceCurrency: "USD",
        billingDuration: "P1Y",
        description: "65,000 credits upfront, 20 workspaces, team collaboration, 50% savings vs monthly",
      },
    ],
    featureList: [
      "TikTok content discovery",
      "Instagram content discovery",
      "AI-powered viral content analysis",
      "Creative brief generation",
      "Team workspaces and collaboration",
      "Content bookmarking and organization",
      "Engagement data analysis",
      "Trend discovery",
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "ViraLens",
    url: "https://app.viralens.ai",
    logo: "https://app.viralens.ai/viralens-icon.png",
    description: "AI-powered content research and creative brief platform for marketing agencies.",
    sameAs: [],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ViraLens",
    url: "https://app.viralens.ai",
    inLanguage: ["en", "es"],
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://app.viralens.ai/?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to research viral content and generate creative briefs with ViraLens",
    description:
      "A three-step workflow to discover viral TikTok and Instagram content, analyze it with AI, and turn the insights into ready-to-use creative briefs.",
    totalTime: "PT5M",
    tool: [{ "@type": "HowToTool", name: "ViraLens" }],
    step: [
      {
        "@type": "HowToStep",
        position: 1,
        name: "Search TikTok and Instagram",
        text: "Enter a keyword or hashtag and ViraLens scrapes TikTok and Instagram for the most engaging recent posts, with filters for date range and platform.",
        url: "https://app.viralens.ai/#how-it-works",
      },
      {
        "@type": "HowToStep",
        position: 2,
        name: "Analyze with Gemini AI",
        text: "ViraLens runs Gemini AI across the results to surface viral patterns, hooks, formats, and engagement signals from the highest-performing content.",
        url: "https://app.viralens.ai/#how-it-works",
      },
      {
        "@type": "HowToStep",
        position: 3,
        name: "Generate a creative brief",
        text: "Turn the AI analysis into a ready-to-use creative brief that your team can hand to creators or producers in minutes.",
        url: "https://app.viralens.ai/#how-it-works",
      },
    ],
  },
  ...[
    { name: "ViraLens Pro Monthly", description: "2,000 credits per month, 5 workspaces, AI analysis, creative brief generation.", price: "49.00", billing: "P1M" },
    { name: "ViraLens Agency Monthly", description: "5,000 credits per month, 20 workspaces, team collaboration, priority support.", price: "99.00", billing: "P1M" },
    { name: "ViraLens Pro Annual", description: "25,000 credits upfront, 5 workspaces, 50% savings vs monthly.", price: "297.00", billing: "P1Y" },
    { name: "ViraLens Agency Annual", description: "65,000 credits upfront, 20 workspaces, team collaboration, 50% savings vs monthly.", price: "600.00", billing: "P1Y" },
  ].map((plan) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    name: plan.name,
    description: plan.description,
    brand: { "@type": "Brand", name: "ViraLens" },
    offers: {
      "@type": "Offer",
      price: plan.price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: "https://app.viralens.ai/#pricing",
      billingDuration: plan.billing,
    },
  })),
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#07070a] relative">
      <SEO
        title="AI-Powered Content Research & Creative Brief Platform"
        description="ViraLens scrapes TikTok and Instagram for viral content, analyzes trends with Gemini AI, and generates ready-to-use creative briefs for marketing agencies. Plans from $49/mo."
        path="/"
        jsonLd={LANDING_JSON_LD}
      />
      <BackgroundEffects />
      <div className="relative z-[2]">
        <Navbar />
        <main>
          <HeroSection />
          <SectionDivider />
          <ShowcaseStrip />
          <SectionDivider />
          <ValueProposition />
          <SectionDivider />
          <ProductDemo />
          <SectionDivider />
          <HowItWorks />
          <SectionDivider />
          <StatsBanner />
          <SectionDivider />
          <PricingSection />
          <SectionDivider />
          <FAQSection />
          <SectionDivider />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </div>
  );
}
